// Builds the `documentScripts` array of the export submission: every
// PDF-side (Acrobat) document script for the export, fully hydrated with the
// values the frontend knows, in attach order. Newer generators attach these
// verbatim instead of assembling scripts from their embedded resources.
//
// Token contract: the frontend fills every `{token}` it knows before
// sending. Exactly four tokens MUST be left verbatim for the generator,
// because only it knows their values: {s3Key}, {referrer}, {pageCounts},
// {totalAppendedPdfPages}. ({email}, {subject} and {body} in
// quick-compare.js are placeholders the PDF script itself resolves at
// runtime — neither side touches them.)
//
// This mirrors the generator's legacy preparePdfScripts() /
// fillScriptsTemplate() (PdfGenerator.java): same value sources, same
// defaults, same fill-then-cleanup order. The jar's sanitizeData() doubles
// backslashes only so they survive Java's replaceAll() replacement
// processing — the net effect is verbatim insertion, which the split/join
// below gives directly.
import cleanupPdfScript from "./cleanup";
import bijScripts from "./scripts-bij";
import quickCompareScripts from "./quick-compare";
import { sanitizeFindByFeaturesExportData } from "./sanitize";

// Defaults of the jar's com.bitbot.aws.FindByFeaturesConfig default
// constructor. The jar deserializes into a default-constructed instance, so
// fields the submission omits keep exactly these values — replicate them
// per field.
const findByFeaturesDefaults = {
  partialMatches: false,
  primaryMatchColor: "",
  secondaryMatchColor: "",
  highlightHeight: 0,
  visibilityColor: "#ededed"
};

function orDefault(value, fallback) {
  return value === undefined || value === null ? fallback : value;
}

// The inlined values land inside a script that Acrobat's pre-ES2019 engine
// parses, where U+2028/U+2029 inside a string literal are line terminators
// and break the parse. JSON.stringify leaves both raw, so escape them here.
// Everything else JSON.stringify already escapes (quotes, backslashes,
// control characters), and the non-JSON values (locale, colors, filter
// names, page index lists) are plain identifiers-and-punctuation strings.
//
// Forward slashes are escaped as "\/" (identical string value in
// JavaScript, and the JSON spec allows the escape) because the script is
// compacted by cleanup.js afterwards \u2014 a mirror of the generator's
// cleanupScripts(), whose comment regex eats everything from a
// whitespace-preceded "//" to the end of the line. CMS text like
// "Yes // with adapter" inside visibilityOptions or quickCompareMapData
// used to truncate the shipped script mid-statement (a dead, unparseable
// PDF script). With no bare "/" left, no "//" can survive into the
// cleanup pass \u2014 here or in the generator's own server-side pass, which
// runs the same regex over the final script. Slashes only ever occur
// inside string-literal positions of the inlined values, so the escape is
// semantics-preserving.
function escapeForInlineScript(value) {
  return String(value)
    .replace(/\//g, "\\/")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

// The three color tokens are inlined inside double-quoted string literals
// and are the only non-JSON values whose provenance (theme/config objects)
// is not under this module's control: a value carrying a quote, backslash
// or line break would end the literal early and corrupt the script. Colors
// are #hex or rgb()/rgba() strings \u2014 anything outside that alphabet falls
// back to the jar default for the token (hex2rgb would have rendered such
// a value as white anyway).
function safeColorToken(value, fallback) {
  return typeof value === "string" && /^[#a-zA-Z0-9(),.% -]*$/.test(value)
    ? value
    : fallback;
}

// Literal, all-occurrences token substitution. split/join (unlike
// String.replace with a string pattern) replaces every occurrence and never
// interprets `$` sequences in the value.
function fillTokens(scripts, tokens) {
  return Object.keys(tokens).reduce(
    (acc, token) =>
      acc.split("{" + token + "}").join(escapeForInlineScript(tokens[token])),
    scripts
  );
}

// {pageCounts} and {totalAppendedPdfPages} are deliberately absent from the
// token map: the generator substitutes them server-side.
//
// Before inlining, the find-by-features data is run through the contract
// sanitizer (./sanitize.js): attributes referencing filter fields absent
// from the export and products without a widget/page target are dropped
// with a console.warn each — cleanup and proceed, never throw, never block
// the export.
function hydrateBijScripts(additionalData) {
  const findByFeatures = additionalData.findByFeaturesConfig || {};
  const sanitized = sanitizeFindByFeaturesExportData(additionalData);
  return cleanupPdfScript(
    fillTokens(bijScripts, {
      annotationPageIndices: orDefault(additionalData.recModelsPages, ""),
      mappedModelsForBrandAndModel: orDefault(
        sanitized.mappedCompetingModels,
        ""
      ),
      // The jar turns an empty/missing value into "[]" (fillScriptsTemplate)
      mappedHideLogic: sanitized.visibilityOptions || "[]",
      productFilters: orDefault(sanitized.productFilterNames, ""),
      partialMatches: orDefault(
        findByFeatures.partialMatches,
        findByFeaturesDefaults.partialMatches
      ),
      color_primaryMatch: safeColorToken(
        orDefault(
          findByFeatures.primaryMatchColor,
          findByFeaturesDefaults.primaryMatchColor
        ),
        findByFeaturesDefaults.primaryMatchColor
      ),
      color_secondaryMatch: safeColorToken(
        orDefault(
          findByFeatures.secondaryMatchColor,
          findByFeaturesDefaults.secondaryMatchColor
        ),
        findByFeaturesDefaults.secondaryMatchColor
      ),
      highlightHeight: orDefault(
        findByFeatures.highlightHeight,
        findByFeaturesDefaults.highlightHeight
      ),
      color_visibility: safeColorToken(
        orDefault(
          findByFeatures.visibilityColor,
          findByFeaturesDefaults.visibilityColor
        ),
        findByFeaturesDefaults.visibilityColor
      )
    })
  );
}

// {s3Key} and {referrer} are deliberately absent from the token map: the
// generator substitutes them server-side.
function hydrateQuickCompareScripts(additionalData, projectType, locale) {
  return cleanupPdfScript(
    fillTokens(quickCompareScripts, {
      quickCompareMapData: additionalData.quickCompareMapData,
      // The jar defaults a missing type to "bij" — the frontend always has
      // projectType, so this default is belt-and-braces only.
      pdfType: orDefault(projectType, "bij"),
      locale: orDefault(locale, "")
    })
  );
}

// Attach conditions mirror the generator's legacy assembly (run_bij in
// PdfGenerator.java) with what the frontend knows:
//
// - EVERY project type gets the bij script: the generator's run() calls
//   run_bij unconditionally regardless of type — its run_scanners method has
//   no callers, so scanners exports too have always shipped scripts_bij.
//   (p-series and t-series land here the same way; the generator also
//   defaults a missing type to "bij".)
// - It attaches when model/visibility data is present or promo PDFs are
//   appended. The one condition the frontend cannot know is the generator's
//   server-side page count of the appended promos
//   ({pageCounts}/{totalAppendedPdfPages}); promo links present in the
//   submission is the closest frontend-known equivalent, differing only
//   when a promo fails to load server-side.
// - The quick-compare script is appended after the base script when
//   quickCompareMapData is present, never alone — the legacy assembly only
//   appends it while preparing a base script.
export default function buildDocumentScripts(
  projectType,
  additionalData,
  locale
) {
  const data = additionalData || {};
  const documentScripts = [];

  const promoLinks = data.allPdfPromoLinks;
  const promosAppended = Array.isArray(promoLinks) && promoLinks.length > 0;
  if (data.mappedCompetingModels || data.visibilityOptions || promosAppended)
    documentScripts.push(hydrateBijScripts(data));

  if (documentScripts.length > 0 && data.quickCompareMapData)
    documentScripts.push(hydrateQuickCompareScripts(data, projectType, locale));

  return documentScripts;
}
